// 🟢 PWACliente con lógica robusta de "vuelta actual" + onSnapshot
import React, { useEffect, useState } from "react";
import { collection, query, getDocs, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../asset/logoSumaSalto.png";
import { Link } from "react-router-dom";
import TicketQRScanner from "./TicketQRScanner";

export default function PWACliente() {
  const [cliente, setCliente] = useState(null);
  const [comercios, setComercios] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [error, setError] = useState(""); // 👈 nuevo estado de error
  const [showScanner, setShowScanner] = useState(false);
  const [qrDebug, setQrDebug] = useState("");
  const [qrStatus, setQrStatus] = useState(""); // loading | ok | error
  const [qrError, setQrError] = useState("");

const handleQRDecoded = async (qrString) => {
  try {
    setQrStatus("loading");
    setQrError("");

    // 1️⃣ Decode completo
    const decoded = decodeURIComponent(qrString);

    // 2️⃣ Extraer parte útil
    const query = decoded.split("?")[1];
    if (!query) throw new Error("QR inválido");

    // 3️⃣ Parseo POSICIONAL DGI
    const parts = query.split(",");

    if (parts.length < 7) {
      throw new Error("Formato de ticket DGI inválido");
    }

    const payload = {
      rut: parts[0],                 // RUT comercio
      idTicket: parts[3],             // Nro CFE
      monto: Number(parts[4]),        // Monto
      ciCliente: ci,                  // Cliente logueado
      origen: "QR",
    };

    // 4️⃣ Validación frontend
    if (!payload.rut || !payload.idTicket || payload.monto <= 0) {
      throw new Error("Datos incompletos en el ticket");
    }

    // 5️⃣ POST al backend
    const res = await fetch(
      "https://apisumauy.1montonx2.com/api/EndPointQR.php",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    const text = await res.text(); // 🔑 CLAVE
    const result = JSON.parse(text);

    if (!res.ok || !result.ok) {
      throw new Error(result?.error || "Error procesando el ticket");
    }

    setQrStatus("ok");
    setShowScanner(false);

  } catch (err) {
    console.error("QR ERROR:", err);
    setQrStatus("error");
    setQrError(err.message);
  }
};



  // 🔹 Obtener CI desde URL, localStorage o cookie
const params = new URLSearchParams(window.location.search);
const ciFromUrl = params.get("id");

// Funciones auxiliares para cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

// Primero intentamos localStorage, luego cookies
const storedCi = localStorage.getItem("clienteId") || getCookie("clienteId");
const ci = ciFromUrl || storedCi;

// Guardar en localStorage y cookie si viene de la URL
useEffect(() => {
  if (ciFromUrl) {
    localStorage.setItem("clienteId", ciFromUrl);
    setCookie("clienteId", ciFromUrl);
  }
}, [ciFromUrl]);


// Traer cliente (tiempo real)
useEffect(() => {
  if (!ci) {
    setError("⚠️ No se encontró cliente. Abre la PWA desde el link de tu QR o email.");
    return;
  }

  const ref = doc(db, "clientes", ci);
  const unsub = onSnapshot(
    ref,
    (snap) => {
      if (snap.exists()) {
        setCliente(snap.data());
        setError("");
      } else {
        setCliente(null);
        setError("❌ CI inválida o no registrada");
      }
    },
    (err) => {
      console.error("onSnapshot cliente error:", err);
      setError("Error al cargar los datos del cliente");
    }
  );

  return () => unsub();
}, [ci]);


  // Traer comercios
  useEffect(() => {
    const fetchComercios = async () => {
      try {
        const q = query(collection(db, "comercios"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setComercios(data);
      } catch (error) {
        console.error("Error obteniendo comercios:", error);
      }
    };
    fetchComercios();
  }, []);

  // Traer notificaciones (puede ser en tiempo real también si querés)
  useEffect(() => {
    const fetchNotificaciones = async () => {
      try {
        const q = query(collection(db, "notificaciones"));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setNotificaciones(data);
      } catch (error) {
        console.error("Error obteniendo notificaciones:", error);
      }
    };
    fetchNotificaciones();
  }, []);

  // Rotación automática de notificaciones
  useEffect(() => {
    if (notificaciones.length > 1) {
      const interval = setInterval(() => {
        setVisibleIndex((prev) => (prev + 1) % notificaciones.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [notificaciones]);

  if (!cliente) return <p>Cargando datos del cliente...</p>;

  // ===== LÓGICA DE VUELTAS =====
  const historial = Array.isArray(cliente.historialCanjes) ? cliente.historialCanjes : [];
  const nombresComercios = comercios.map((c) => c.nombre).filter(Boolean);
  const N = nombresComercios.length;

  // Solo nombres que existen hoy
  const historialFiltrado = historial.filter((n) => nombresComercios.includes(n));

  const ultimoNombre = cliente?.ultimoCanje?.comercio || "";

  // Detección híbrida:
  // - Si guardás duplicados (push), los ÚLTIMOS N mostrarán duplicados mientras dura la vuelta.
  // - Si movés al frente (unshift/sin duplicar), los PRIMEROS N no tendrán duplicados; marcamos al menos el último canje.
  const ultimosN = historialFiltrado.slice(-N);
  const primerosN = historialFiltrado.slice(0, N);

  const tieneDup = (arr) => arr.length !== new Set(arr).size;

  let visitadosSet = new Set();

  if (N > 0) {
    if (tieneDup(ultimosN)) {
      // Caso típico con duplicados por "push": usamos los últimos N como progreso de la vuelta
      visitadosSet = new Set(ultimosN);
      // Si ya están todos, reseteamos
      if (visitadosSet.size === N) visitadosSet = new Set();
    } else if (tieneDup(primerosN)) {
      // Caso poco común de duplicados al frente: usamos los primeros N
      visitadosSet = new Set(primerosN);
      if (visitadosSet.size === N) visitadosSet = new Set();
    } else {
      // Lista única (sin duplicados). No podemos inferir la vuelta exacta.
      // Para feedback inmediato, marcamos al menos el último comercio canjeado.
      if (ultimoNombre && nombresComercios.includes(ultimoNombre)) {
        visitadosSet = new Set([ultimoNombre]);
      } else {
        visitadosSet = new Set();
      }
    }
  }

  return (
    <>
      <style>{`
        @keyframes shine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .shiny-number {
          font-size: 4.5rem;
          font-weight: bold;
          background: linear-gradient(80deg, #635506ff, #ebbf6eff, #e2cf61ff);
          background-size: 200% auto;
          color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          animation: shine 5s linear infinite;
        }
        .card {
          background-color: #fff;
          width: 100%;
          max-width: 320px;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 16px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }
        .mini-pwa {
          border-radius: 12px;
          background: #f9f9f9;
          padding: 12px;
          max-height: 200px;
          overflow: auto;
        }
      `}</style>

      <div
        style={{
          backgroundColor: "#b8dfb8ff",
          aspectRatio: "9 / 16",
          maxWidth: "400px",
          width: "100%",
          margin: "20px auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
          borderRadius: "20px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.2)"
        }}
      >
        {/* Logo */}
        <img
          src={logo}
          alt="SumaSalto"
          style={{ borderRadius: "12px", width: "180px", marginBottom: "16px" }}
        />

        {/* Nombre */}
        <h2 style={{ color: "#2f4f2f", fontSize: "1.2rem", marginBottom: "20px" }}>
          Hola, {cliente.nombre}
        </h2>

        {/* Puntos */}
        <div className="card" style={{ textAlign: "center" }}>
          <h1 className="shiny-number">{cliente.puntos}</h1>
          <p style={{ color: "#777" }}>puntos</p>
        </div>

{/* Ticket QR - Componente Padre */}
<div className="card" style={{ textAlign: "center" }}>
  <h3>📷 Cargar puntos con ticket</h3>

  {!showScanner && (
    <button
      onClick={() => setShowScanner(true)}
      style={{
        marginTop: "10px",
        background: "#2f4f2f",
        color: "#fff",
        border: "none",
        padding: "10px 14px",
        borderRadius: "10px",
        cursor: "pointer",
      }}
    >
      Escanear ticket
    </button>
  )}

  {/* Zona de depuración para ver si llega el dato */}
  {qrDebug && (
      <div style={{ marginTop: 20, padding: 10, background: "#f0f0f0", wordBreak: "break-all" }}>
          <strong>Dato capturado:</strong>
          <pre>{qrDebug}</pre>
      </div>
  )}

  {showScanner && (
  <TicketQRScanner
  onResult={(text) => {
    setQrDebug(text);       // debug visual
    handleQRDecoded(text); // 🔥 lógica real
  }}
  onClose={() => setShowScanner(false)}
/>

  )}


  {qrDebug && (
    <div
      style={{
        marginTop: "10px",
        fontSize: "0.8rem",
        wordBreak: "break-all",
        background: "#f5f5f5",
        padding: "6px",
        borderRadius: "6px",
      }}
    >
      <b>QR leído:</b>
      <br />
      {qrDebug}

      {qrStatus === "loading" && (
  <p style={{ color: "#555" }}>⏳ Procesando ticket...</p>
)}

{qrStatus === "ok" && (
  <p style={{ color: "green" }}>✅ Ticket procesado correctamente</p>
)}

{qrStatus === "error" && (
  <p style={{ color: "red" }}>❌ {qrError}</p>
)}

    </div>
  )}
</div>


        {/* Comercios */}
        <div className="card">
          <div className="mini-pwa">
            <h3>🏪 Comercios</h3>
            <ul style={{ paddingLeft: "20px", maxHeight: "200px",  overflowY: "auto" }}>
              {comercios.map((c, i) => {
                const visitado = visitadosSet.has(c.nombre);
                // buscamos en el mapa valorCanjeVuelta del cliente
                const valorCanje = cliente?.valorCanjeVuelta?.[c.nombre] ?? null;

                return (
                  <li
                    key={i}
                    title={visitado ? "Ya visitado, próximamente" : ""}
                    style={{
                      color: visitado ? "#aaa" : "#000",
                      fontWeight: visitado ? "normal" : "bold",
                      background: visitado ? "#f0f0f0" : "#eaffea",
                      padding: "6px 10px",
                      borderRadius: "8px",
                      marginBottom: "6px",
                      cursor: visitado ? "not-allowed" : "default",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <span>{c.nombre}</span>

                    {valorCanje !== null && valorCanje !== undefined ? (
                      <span style={{ fontSize: "0.85rem", color: "#2f4f2f", marginLeft: "8px" }}>
                        💰 ${valorCanje}
                      </span>
                    ) : (
                      // si no hay valor guardado, mostramos un espacio vacío o un guion suave
                      <span style={{ fontSize: "0.8rem", color: "#bbb", marginLeft: "8px" }}>
                        —
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Notificaciones */}
        <div className="card">
          <div className="mini-pwa">
            <h3>🔔 Notificaciones</h3>
            <div style={{ position: "relative", height: "60px" }}>
              <AnimatePresence mode="wait">
                {notificaciones.length > 0 && (
                  <motion.div
                    key={visibleIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.6 }}
                    style={{
                      position: "absolute",
                      width: "100%",
                      background: "#fff",
                      padding: "6px 10px",
                      borderRadius: "8px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      fontSize: "0.9rem"
                    }}
                  >
                    🏪 <b>{notificaciones[visibleIndex].nombreComercio}</b> —{" "}
                    {notificaciones[visibleIndex].mensaje}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        

{/* Footer */}
<footer style={{ marginTop: "20px", textAlign: "center" }}>
  <Link
    to="/info"
    style={{
      color: "#2f4f2f",
      textDecoration: "none",
      fontWeight: "bold",
    }}
  >
    Más información
  </Link>
</footer>

      </div>
    </>
  );
}








